import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

import { GET_JOINED_TEAMS, GET_USER } from '../../contracts';

const CRITICAL_KEYS = [GET_USER, GET_JOINED_TEAMS] as const;

/**
 * Whether the new workspace's critical data has landed since a workspace load began
 * at `startedAt`. The user context and joined teams are the two
 * workspace-scoped queries the app resolves first for a workspace, and their keys
 * are contract, so this never reaches into the resolver that fires them.
 *
 * A query counts as settled once it is not fetching and has either data or
 * an error newer than the load — a failed fetch releases the overlay to the
 * app's own error handling rather than holding it to the cap. Read straight
 * off the cache through a subscription rather than through query observers,
 * so mounting this never changes which observer owns a `queryFn`.
 */
export function useWorkspaceLoadSettled(startedAt: number): boolean {
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) =>
      queryClient.getQueryCache().subscribe((event) => {
        const key = event?.query?.queryKey as unknown;
        if (Array.isArray(key) && CRITICAL_KEYS.some((critical) => critical === key[0])) {
          onChange();
        }
      }),
    [queryClient]
  );
  const read = useCallback(
    () => isCriticalDataSettled(queryClient, startedAt),
    [queryClient, startedAt]
  );
  return useSyncExternalStore(subscribe, read, read);
}

function isCriticalDataSettled(queryClient: QueryClient, startedAt: number): boolean {
  return CRITICAL_KEYS.every((key) => {
    const state = queryClient.getQueryState([key]);
    if (!state || state.fetchStatus === 'fetching') return false;
    return Math.max(state.dataUpdatedAt, state.errorUpdatedAt) > startedAt;
  });
}
